import { useRef, useState } from 'react';
import { Camera, ImageUp, Keyboard, Mic, WifiOff } from 'lucide-react';
import type { Formulation, Instrument, ProjectReader } from '../lib/types';
import { readerSnapshot } from '../services/readers';
import { prepareImage } from '../lib/image';
import { enqueue } from '../lib/offlineQueue';
import { createCapture, newCaptureId } from '../services/captures';
import { useAuth } from '../hooks/useAuth';
import { uploadCaptureImage } from '../services/storage';
import { updateDoc } from 'firebase/firestore';
import { captureDoc } from '../services/captures';
import { isSpeechSupported } from '../hooks/useSpeech';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { requestNativeCamera } from '../lib/nativeBridge';
import { Button, cx } from './ui';
import type { ConfirmationSource } from './ConfirmationScreen';
import VoiceCapture from './VoiceCapture';
import type { ReaderSession } from './ReaderCapture';

export default function CaptureBar({
  uid,
  projectId,
  formulations,
  readers,
  onCaptured,
  onReaderSession,
}: {
  uid: string;
  projectId: string;
  formulations: Formulation[];
  /**
   * What a capture can be read as: the project's chosen readers, or on an
   * older project, the signed-in person's own.
   */
  readers: ProjectReader[];
  onCaptured: (source: ConfirmationSource) => void;
  /**
   * Set on a project that records against readers. Manual and voice then go to
   * the reader screens, against the selected reader's fields, instead of the
   * fixed formulation fields.
   */
  onReaderSession?: (session: ReaderSession) => void;
}) {
  /**
   * Which reader a capture is read against, replacing the fixed
   * Zetasizer/Encapsulation toggle. Defaults to the first rather than
   * remembering a preference: picking the wrong reader shows up immediately on
   * the confirmation screen, whereas a silently remembered one does not.
   */
  const [readerId, setReaderId] = useState<string>('');
  const reader = readers.find((r) => r.id === readerId) ?? readers[0] ?? null;
  // Kept on the capture for continuity with records written before readers.
  const instrument: Instrument = 'unknown';
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const speechSupported = isSpeechSupported();
  /**
   * On a computer this bar is review furniture, not capture furniture.
   *
   * Readings are taken at the bench, on a phone: that is where the camera and
   * the microphone are, and where hands are gloved. A desktop session is for
   * reading the table, correcting a digit and exporting — so the capture modes
   * that need hardware are hidden there rather than offered and then failing on
   * a machine with no usable camera. Manual entry stays, because correcting a
   * value is exactly what a desktop session is for.
   */
  const isDesktop = useIsDesktop();

  /**
   * Android will not open the capture intent while CAMERA is declared but not
   * granted, so the permission is settled before the picker opens rather than
   * letting the tap silently do nothing.
   */
  async function openCamera() {
    setNotice(null);
    const result = await requestNativeCamera();
    if (!result.granted) {
      setNotice(
        result.canAskAgain === false
          ? 'Camera access is off for BlisxBench. Turn it on in your device settings, or use Manual entry.'
          : 'Camera access is needed to photograph the screen. Manual entry always works.',
      );
      return;
    }
    cameraRef.current?.click();
  }

  /**
   * Choosing an existing image is a separate input from the camera on purpose.
   * `capture="environment"` makes a phone open the camera and hide the gallery
   * entirely, so a screenshot of a results spreadsheet — the most common way a
   * table arrives — cannot be selected at all through the camera input. This
   * one carries no `capture` attribute and needs no camera permission.
   */
  function openUpload() {
    setNotice(null);
    uploadRef.current?.click();
  }

  async function handlePhoto(file: File) {
    setBusy(true);
    setNotice(null);
    const captureId = newCaptureId(projectId);

    // The confirmation screen opens immediately so the wait is visible from the
    // first tap rather than after the upload finishes.
    const base: ConfirmationSource = {
      mode: 'photo',
      captureId,
      instrument,
      readerId: reader?.id ?? null,
      readerName: reader?.name ?? null,
      extraction: null,
      transcript: null,
      previewUrl: null,
      storagePath: null,
      targetFormulationId: null,
      focusKey: null,
      extracting: true,
      progress: { phase: 'compressing' },
    };
    onCaptured(base);

    try {
      const prepared = await prepareImage(file);
      onCaptured({ ...base, previewUrl: prepared.previewUrl, progress: { phase: 'uploading', fraction: 0 } });

      // The capture record is written first and works offline — Firestore's
      // local cache accepts it and replays on reconnect.
      await createCapture(projectId, captureId, {
        mode: 'photo',
        instrument,
        // Snapshot, so a colleague can read these results without access to the
        // reader that produced them.
        ...(reader ? { reader: readerSnapshot(reader) } : {}),
        capturedByUid: uid,
        capturedByName: user?.displayName ?? user?.email ?? null,
        status: 'pending',
      });

      if (!navigator.onLine) {
        // Capture must never block on connectivity. Queue the bytes and move on.
        await enqueue({
          captureId,
          uid,
          projectId,
          instrument,
          blob: prepared.full,
          thumbBlob: prepared.thumb,
          createdAt: Date.now(),
        });
        setNotice('Offline — photo queued. It will extract automatically when you reconnect.');
        return;
      }

      const { storagePath, thumbnailPath, downloadUrl } = await uploadCaptureImage(
        uid,
        captureId,
        prepared.full,
        prepared.thumb,
        (fraction) =>
          onCaptured({
            ...base,
            previewUrl: prepared.previewUrl,
            progress: { phase: 'uploading', fraction },
          }),
      );
      await updateDoc(captureDoc(projectId, captureId), {
        storagePath,
        thumbnailPath,
        downloadUrl,
      });

      /**
       * The photo is up, but nothing is read yet. Reading happens on the
       * confirmation screen, once the user has had the chance to add a spoken
       * or typed note — the note has to travel with the first extraction to be
       * worth anything, and there is no way back to "before the first read".
       * Reading with no note is a single tap, so the common case stays fast.
       */
      onCaptured({
        ...base,
        previewUrl: prepared.previewUrl,
        storagePath,
        extracting: false,
        progress: undefined,
        awaitingNote: true,
      });
    } catch (e) {
      setNotice((e as Error)?.message ?? 'Could not process that photo.');
    } finally {
      setBusy(false);
      // Clearing both lets the same file be picked twice in a row; without it
      // the change event never fires the second time.
      if (cameraRef.current) cameraRef.current.value = '';
      if (uploadRef.current) uploadRef.current.value = '';
    }
  }

  function startManual() {
    if (onReaderSession) {
      if (reader) onReaderSession({ kind: 'manual', reader });
      return;
    }
    onCaptured({
      mode: 'manual',
      captureId: null,
      instrument,
      extraction: null,
      transcript: null,
      previewUrl: null,
      storagePath: null,
      targetFormulationId: formulations.length === 1 ? formulations[0].id : null,
      focusKey: null,
    });
  }

  return (
    <>
      {/* pb clears the home indicator / navigation buttons; without it the
          capture buttons sit underneath the system bar and cannot be tapped. */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-ink-800 bg-ink-900/95 backdrop-blur"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="mx-auto max-w-6xl px-4 pb-3 pt-3">
          <div className="mb-2.5 flex items-center gap-2">
            <span className="field-label shrink-0">Reading from</span>
            {readers.length === 0 ? (
              <span className="text-xs text-ink-400">
                {onReaderSession
                  ? 'Choose what this project records first.'
                  : 'No readers yet — add one under Readers.'}
              </span>
            ) : (
              <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto rounded-lg bg-ink-800 p-0.5">
                {readers.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setReaderId(r.id)}
                    title={r.description || r.fields.map((f) => f.label).join(', ')}
                    className={cx(
                      'shrink-0 rounded-md px-3 py-1 text-xs font-medium transition-colors',
                      'inline-flex items-center pointer-coarse:min-h-11 pointer-coarse:px-4',
                      reader?.id === r.id
                        ? 'bg-accent-500 text-white'
                        : 'text-ink-400 hover:text-ink-100',
                    )}
                  >
                    {r.name || 'Untitled'}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={cx('grid gap-2', isDesktop ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-4')}>
            {!isDesktop && (
              <Button
                variant="primary"
                size="lg"
                icon={<Camera size={18} />}
                loading={busy}
                onClick={() => void openCamera()}
              >
                Photo
              </Button>
            )}
            {/* Screenshots and exported sheets arrive far more often than
                photographs of a screen, so uploading is a peer of the camera,
                not something buried behind it. */}
            {!isDesktop && (
              <Button
                variant="secondary"
                size="lg"
                icon={<ImageUp size={18} />}
                disabled={busy}
                onClick={openUpload}
              >
                Upload
              </Button>
            )}
            {!isDesktop && (
              <Button
                variant="secondary"
                size="lg"
                icon={speechSupported ? <Mic size={18} /> : <WifiOff size={18} />}
                disabled={!speechSupported}
                title={speechSupported ? undefined : 'This browser has no speech recognition'}
                onClick={() => setVoiceOpen(true)}
              >
                Voice
              </Button>
            )}
            {/* Manual is never hidden behind another mode — it is the fallback
                for every failure and must always be one tap away. */}
            <Button
              variant={isDesktop ? 'primary' : 'secondary'}
              size="lg"
              icon={<Keyboard size={18} />}
              onClick={startManual}
            >
              {isDesktop ? 'Add or edit a reading' : 'Manual'}
            </Button>
          </div>

          {isDesktop && (
            <p className="mt-2 text-center text-xs text-ink-400">
              Photo and voice capture are on the phone — this is the bench view for reviewing,
              correcting and exporting.
            </p>
          )}

          {notice && <p className="mt-2 text-center text-xs text-amber-300">{notice}</p>}
        </div>
      </div>

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handlePhoto(file);
        }}
      />

      {/* No `capture` attribute: this is the one that opens the gallery and the
          file picker, so a screenshot or an exported sheet can be chosen. */}
      <input
        ref={uploadRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handlePhoto(file);
        }}
      />

      <VoiceCapture
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        uid={uid}
        projectId={projectId}
        instrument={instrument}
        onCaptured={onCaptured}
        reader={onReaderSession ? reader : null}
        onReaderVoice={
          onReaderSession && reader
            ? (captureId, transcript) =>
                onReaderSession({ kind: 'voice', reader, captureId, transcript })
            : undefined
        }
      />
    </>
  );
}
